'use client';

import { cn } from '../../../../lib/utils';
import React from 'react';
import ToolButton from '../../../primitives/buttons/tool-button/ToolButton';
import { Close } from '@crabs-ai/web-icons';
import Button from '../../../primitives/buttons/global-button/Button';

/* ========================================
 * Context
 * ======================================== */

interface ContextBarContextValue {
  isVisible?: boolean;
}

const ContextBarContext = React.createContext<ContextBarContextValue>({});

const useContextBarContext = () => {
  const context = React.useContext(ContextBarContext);
  if (!context) {
    throw new Error(
      'ContextBar 서브 컴포넌트는 ContextBar 내부에서 사용되어야 합니다.',
    );
  }
  return context;
};

/* ========================================
 * ContextBar (메인 컨테이너)
 * ======================================== */

export interface ContextBarProps extends React.ComponentProps<'div'> {
  /** 표시 여부 (CSS transition 제어) */
  isVisible?: boolean;
  /** 애니메이션 비활성화 */
  disableAnimation?: boolean;
}

function ContextBarRoot({
  isVisible = true,
  disableAnimation = false,
  className,
  children,
  ...props
}: ContextBarProps) {
  const contextValue = React.useMemo(() => ({ isVisible }), [isVisible]);

  return (
    <ContextBarContext.Provider value={contextValue}>
      <div
        data-slot="context-bar"
        data-visible={isVisible}
        className={cn(
          // 기본 스타일 (피그마 기준)
          'flex w-fit items-center gap-2xs rounded-sm border border-sys-style-neutral-default bg-sys-bg-surface-1 px-xs py-2xs inset-shadow-thick-md',
          // 애니메이션
          !disableAnimation && 'transition-all duration-300 ease-out',
          isVisible
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-4 opacity-0',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </ContextBarContext.Provider>
  );
}

/* ========================================
 * ContextBar.CloseButton
 * ======================================== */

export interface ContextBarCloseButtonProps {
  /** 클릭 핸들러 */
  onClick?: () => void;
  /** 툴팁 텍스트 */
  tooltipNode?: string | React.ReactNode;
  /** 툴팁 표시 여부 */
  isShowTooltip?: boolean;
  /** 추가 className */
  className?: string;
}

function ContextBarCloseButton({
  onClick,
  tooltipNode,
  isShowTooltip = false,
  className,
}: ContextBarCloseButtonProps) {
  return (
    <ToolButton
      state="default"
      size="md"
      onClick={onClick}
      isShowTooltip={isShowTooltip}
      tooltipNode={tooltipNode}
      className={className}
    >
      <Close className="size-[24px] [&_path]:fill-sys-cont-neutral-default" />
    </ToolButton>
  );
}

/* ========================================
 * ContextBar.Counter
 * ======================================== */

export interface ContextBarCounterProps extends React.ComponentProps<'div'> {
  /** 선택된 개수 */
  count: number;
  /** 레이블 텍스트 (예: "선택함", "개 선택") */
  label?: string;
  /** 숫자 강조 색상 */
  accentColor?: 'primary' | 'neutral';
}

function ContextBarCounter({
  count,
  label = '선택함',
  accentColor = 'primary',
  className,
  ...props
}: ContextBarCounterProps) {
  return (
    <div
      data-slot="context-bar-counter"
      className={cn(
        'flex items-center gap-5xs text-comp-accent-medium',
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          accentColor === 'primary'
            ? 'text-sys-cont-primary-default'
            : 'text-sys-cont-neutral-default',
        )}
      >
        {count}개
      </span>
      <span className="text-sys-cont-neutral-default">{label}</span>
    </div>
  );
}

/* ========================================
 * ContextBar.Divider
 * ======================================== */

export interface ContextBarDividerProps extends React.ComponentProps<'div'> {}

function ContextBarDivider({ className, ...props }: ContextBarDividerProps) {
  return (
    <div
      data-slot="context-bar-divider"
      className={cn('h-[16px] w-[1px] bg-sys-style-neutral-deep', className)}
      {...props}
    />
  );
}

/* ========================================
 * ContextBar.Actions
 * ======================================== */

export interface ContextBarActionsProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
}

function ContextBarActions({
  className,
  children,
  ...props
}: ContextBarActionsProps) {
  // Button 컴포넌트에 기본 size="small" 주입
  const enhancedChildren = React.Children.map(children, (child) => {
    if (
      React.isValidElement<React.ComponentProps<typeof Button>>(child) &&
      child.type === Button
    ) {
      return React.cloneElement(child, {
        size: child.props.size ?? 'small',
      });
    }
    return child;
  });

  return (
    <div
      data-slot="context-bar-actions"
      className={cn('flex items-center gap-2xs', className)}
      {...props}
    >
      {enhancedChildren}
    </div>
  );
}

/* ========================================
 * Compound Component Export
 * ======================================== */

export const ContextBar = Object.assign(ContextBarRoot, {
  CloseButton: ContextBarCloseButton,
  Counter: ContextBarCounter,
  Divider: ContextBarDivider,
  Actions: ContextBarActions,
});

export {
  ContextBarRoot,
  ContextBarCloseButton,
  ContextBarCounter,
  ContextBarDivider,
  ContextBarActions,
  useContextBarContext,
};
